import React, {
  useMemo,
  useRef,
  useState,
} from 'react';
import { describeArc } from '../../utils/arc';
import DonutChartTooltip from '../donut-chart-tooltip/DonutChartTooltip';

interface Segment {
  key: string;
  percentage?: number;
  color?: string;
  tooltip?: string;
  offset: number;
}
interface DonutChartArcProps {
  segment: Segment;
  centerX: number;
  centerY: number;
  angle: number;
  className?: string;
  parentRef: SVGSVGElement | null;
}
function DonutChartArc({
  segment,
  centerX,
  centerY,
  angle,
  className,
  parentRef,
}: DonutChartArcProps): JSX.Element {
  const d = useMemo(() => describeArc(
    centerX,
    centerY,
    angle,
    3.6 * segment.offset,
    3.6 * segment.offset + 3.6 * (segment.percentage || 0),
  ), [segment, centerX, centerY, angle]);
  const groupRef = useRef<SVGGElement | null>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const rect = useMemo(() => {
    const svgRect = parentRef?.getBoundingClientRect();
    const BBox = parentRef?.getBBox();
    return ({
      width: svgRect?.width || 1,
      height: svgRect?.height || 1,
      ratioX: 42 / (svgRect?.width || 1),
      ratioY: 42 / (svgRect?.height || 1),
      x: svgRect?.left || 0,
      y: svgRect?.top || 0,
      stroke: ((BBox?.width || 1) / (svgRect?.width || 1)) * 1,
    });
  }, [parentRef]);
  const [isTooltipShown, setTooltipShown] = useState(false);
  function onMouseMove(e: React.MouseEvent<SVGPathElement>) {
    if (segment.tooltip) {
      setPosition({
        x: (e.clientX - rect.x) * rect.ratioX,
        y: (e.clientY - rect.y - 10) * rect.ratioY,
      });
    }
  }
  function onMouseEnterArc() {
    setTooltipShown(true);
  }
  function onMouseLeaveArc() {
    setTooltipShown(false);
  }
  return (
    <g ref={groupRef}>
      <path
        d={d}
        className={className}
        fill={segment.color}
        pointerEvents="visiblePainted"
        onMouseMove={onMouseMove}
        onMouseEnter={onMouseEnterArc}
        onMouseLeave={onMouseLeaveArc}
        strokeWidth={rect.stroke}
      />
      { (segment.tooltip && isTooltipShown) && (
        <DonutChartTooltip
          segmentKey={segment.key}
          position={position}
          fontSize={10 * ((rect.ratioX + rect.ratioY) / 2)}
          text={segment.tooltip}
        />
      )}
    </g>
  );
}

export default DonutChartArc;
